import { useState } from "react";
import { toast } from "sonner";
import { ArrowDownToLine, ArrowUpFromLine, ExternalLink } from "lucide-react";
import { useFetch } from "../lib/useFetch";
import { api, errMsg, rm, ts } from "../lib/api";
import { Panel, Btn } from "./common";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

const STEPS = {
  topup: ["Open the moomoo app and log in to your Moomoo MY account", "Tap Accounts → Deposit", "Choose MYR and pay via FPX / DuitNow / bank transfer", "DMI detects the new cash on its next cycle and emails you"],
  withdraw: ["Open the moomoo app and log in to your Moomoo MY account", "Tap Accounts → Withdraw", "Choose MYR, amount and your registered bank account", "DMI detects the lower cash on its next cycle, shrinks sizing and emails you"],
};

function RealMoney({ cs, syncOn }) {
  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="cashflow-real">
      {Object.entries(STEPS).map(([k, steps]) => (
        <div key={k} className="rounded-sm border border-[#E2E8F0] bg-[#F8FAFC] p-3">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-slate-900">{k === "topup" ? <ArrowDownToLine size={14} /> : <ArrowUpFromLine size={14} />}{k === "topup" ? "Deposit real money" : "Withdraw real money"}</div>
          <ol className="list-decimal space-y-1 pl-4 text-[11px] text-slate-600">{steps.map((s) => <li key={s}>{s}</li>)}</ol>
        </div>
      ))}
      <div className="text-[11px] text-slate-600 md:col-span-2">
        <div className="mono text-sm text-slate-900" data-testid="cashflow-real-cash">{cs ? `Moomoo MYR cash ${rm(cs.value)} · synced ${ts(cs.ts)}` : "Moomoo cash not synced"}</div>
        {!syncOn && <div className="mt-1 text-amber-600" data-testid="cashflow-sync-off-warning">Turn on "Follow real Moomoo MY MYR cash" above so DMI detects deposits and withdrawals automatically.</div>}
        <p className="mt-1 text-slate-500">Moomoo does not allow outside apps to move money, so deposits and withdrawals are done in the moomoo app. DMI never holds or moves your funds.</p>
        <a href="https://www.moomoo.com/my" target="_blank" rel="noreferrer" data-testid="cashflow-open-moomoo-link" className="mt-2 inline-flex items-center gap-1 text-blue-600 hover:underline">Open moomoo <ExternalLink size={12} /></a>
      </div>
    </div>
  );
}

function DemoMoney() {
  const { data, reload } = useFetch("/paper/cash-flows");
  const [amt, setAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (kind) => {
    setBusy(true);
    try { await api.post("/paper/cash-flow", { kind, amount: parseFloat(amt) }); toast.success(`Demo ${kind} RM${amt}`); setAmt(""); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const ok = parseFloat(amt) > 0 && !busy;
  return (
    <div data-testid="cashflow-demo">
      <div className="mono text-sm text-slate-900" data-testid="cashflow-demo-cash">Free demo cash {data ? rm(data.cash) : "…"}</div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input data-testid="cashflow-demo-amount" type="number" min="0" step="any" value={amt} onChange={(e) => setAmt(e.target.value)} placeholder="Amount RM" className="mono w-36 rounded-sm border border-[#E2E8F0] bg-[#F8FAFC] px-2 py-1.5 text-xs outline-none focus:border-blue-600" />
        <Btn variant="success" disabled={!ok} onClick={() => go("topup")} data-testid="cashflow-demo-topup-btn"><ArrowDownToLine size={13} />Topup</Btn>
        <Btn variant="ghost" disabled={!ok} onClick={() => go("withdraw")} data-testid="cashflow-demo-withdraw-btn"><ArrowUpFromLine size={13} />Withdraw</Btn>
      </div>
      <p className="mt-1 text-[10px] text-slate-500">Virtual money for practice only. Topups and withdrawals adjust P&L baselines so they never count as profit or loss.</p>
      <div className="mt-2 max-h-40 overflow-auto" data-testid="cashflow-demo-history">
        {(data?.flows || []).map((f) => <div key={f.id} className="mono flex justify-between border-b border-[#EEF2F6] py-1 text-[11px]"><span className={f.kind === "topup" ? "text-emerald-600" : "text-red-600"}>{f.kind === "topup" ? "+" : "−"}{rm(f.amount)}</span><span className="text-slate-500">{ts(f.ts)}</span></div>)}
      </div>
    </div>
  );
}

export const CashFlow = ({ cs, syncOn }) => (
  <Panel title="Topup & Withdraw" className="mt-3" testid="cashflow-panel">
    <Tabs defaultValue="real">
      <TabsList><TabsTrigger value="real" data-testid="cashflow-tab-real">Real money (Moomoo)</TabsTrigger><TabsTrigger value="demo" data-testid="cashflow-tab-demo">Demo capital</TabsTrigger></TabsList>
      <TabsContent value="real"><RealMoney cs={cs} syncOn={syncOn} /></TabsContent>
      <TabsContent value="demo"><DemoMoney /></TabsContent>
    </Tabs>
  </Panel>
);
